import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useClient} from "../../../utils/hooks";
import {AxiosError} from "axios";
import {
  CreateReviewApiResponse,
  CreateReviewPayload,
  CustomerUpdateReviewPayload,
} from "../types";
import {message} from "antd";
import {AnalyticsEvent} from "../../../utils/analytics";
import {
  CUSTOMER_CAN_EDIT_REVIEW_QUERY_KEY,
  CUSTOMER_REVIEW_QUERY_KEY,
} from "../constants";

const mapPayloadToV2 = (payload: CreateReviewPayload) => ({
  firstName: payload.first_name,
  lastName: payload.last_name,
  emailAddress: payload.email_address,
  review: payload.review,
  rating: payload.rating,
  offeringId: payload.offering_id,
  offeringType: payload.offering_type,
});

export const useCreateReviewApi = () => {
  const queryClient = useQueryClient();
  const client = useClient("REVIEWS_AND_RATINGS");
  return useMutation<
    {data: CreateReviewApiResponse},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    CreateReviewPayload,
    unknown
  >({
    mutationFn: (reviewData) =>
      client("public/reviews", {
        method: "POST",
        data: mapPayloadToV2(reviewData),
        token: undefined,
      }),

    onError: (error) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Reviews", "Reviews create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Reviews", "Reviews create success");
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({queryKey: [CUSTOMER_REVIEW_QUERY_KEY]}),
        queryClient.invalidateQueries({queryKey: [CUSTOMER_CAN_EDIT_REVIEW_QUERY_KEY]}),
      ]),
  });
};
const mapUpdatePayloadToV2 = (payload: CustomerUpdateReviewPayload) => ({
  ratingId: payload.ratingId,
  emailAddress: payload.email_address,
  review: payload.review,
  rating: payload.rating,
});

export const useUpdateReviewApi = () => {
  const queryClient = useQueryClient();
  const client = useClient("REVIEWS_AND_RATINGS");
  return useMutation<
    {data: {}},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    CustomerUpdateReviewPayload,
    unknown
  >({
    mutationFn: (reviewData) =>
      client("public/reviews/update", {
        method: "PATCH",
        data: mapUpdatePayloadToV2(reviewData),
        token: undefined,
      }),

    onError: (error) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("Reviews", "Reviews create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Reviews", "Reviews create success");
    },
    onSettled: (d, e, v, c) =>
      queryClient.invalidateQueries({
        queryKey: [
          CUSTOMER_REVIEW_QUERY_KEY,
          {
            emailAddress: v.email_address,
            offering_id: v.offering_id,
            offering_type: v.offering_type,
          },
        ],
      }),
  });
};

const mapFilterPayloadToV2 = (payload: {
  offering_name: string;
  customers_name: string;
  rating: number;
}) => ({
  offeringName: payload.offering_name,
  customersName: payload.customers_name,
  rating: payload.rating,
});

export const useFilterReviews = () => {
  const client = useClient("REVIEWS_AND_RATINGS");
  return useMutation({
    mutationFn: (payload: {
      offering_name: string;
      customers_name: string;
      rating: number;
    }) => {
      const v2Payload = mapFilterPayloadToV2(payload);
      const params = new URLSearchParams({
        offeringName: v2Payload.offeringName,
        customersName: v2Payload.customersName,
        rating: String(v2Payload.rating),
      });
      return client(`public/reviews/filter?${params}`, {
        method: "GET",
      }).then((res) => res.data);
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Coach transactions", "Filter transactions failed");
    },
    onSuccess: () => {
      AnalyticsEvent("Coach transactions", "Filter transactions success");
    },
  });
};
